import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
} from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import SendIcon from '@mui/icons-material/Send';
import { AxiosError } from 'axios';
import DataTable from '../../../../sharedModules/dataTable';
import PageHeader from '../../../../sharedModules/layout/PageHeader';
import { SnackBarContext } from '../../../../sharedModules/form/contexts/SnackBarContext';
import {
  CollectionsCorporateLocation,
  getCollectionsSelectedLocation,
} from '../../../navigation/components/CollectionsLocationToggle';
import { useStatementConfig } from '../../config/hooks/useStatementConfig';
import { useStatementScheduledEmails } from '../hooks/useStatementScheduledEmails';
import { useSendStatementEmail } from '../hooks/useSendStatementEmail';
import { useResumeStatementAutomatic } from '../hooks/useResumeStatementAutomatic';
import { StatementScheduledEmail } from '../model';
import StatementScheduledEmailModal from '../components/StatementScheduledEmailModal';
import StatementScheduledEmailFilters from '../components/StatementScheduledEmailFilters';
import StatementScheduleStatusCell from '../components/StatementScheduleStatusCell';
import {
  ScheduledEmailTableTooltip,
  scheduledEmailTooltipProps,
} from '../../../scheduledEmails/components/ScheduledEmailTableTooltip';
import { relativeTimeUntil } from '../utils';
import {
  EMPTY_STATEMENT_FILTERS,
  filterStatementScheduledEmails,
  StatementScheduledEmailFilterFields,
} from '../filterStatementScheduledEmails';

type ConfirmAction = 'send' | 'resume';

interface ActionInFlight {
  billCode: string;
  action: ConfirmAction;
}

interface ConfirmState {
  open: boolean;
  action: ConfirmAction;
  email: StatementScheduledEmail | null;
  overrideBody?: string;
}

interface LocationTabProps {
  location: CollectionsCorporateLocation;
  enabled: boolean;
}

interface SendActionProps {
  row: StatementScheduledEmail;
  isSending: boolean;
  sendingDisabled: boolean;
  onSendNow: (email: StatementScheduledEmail) => void;
}

const apiErrorMessage = (err: unknown, fallback: string): string => {
  const axiosErr = err as AxiosError<{ error?: string; errors?: string[] }>;
  const body = axiosErr.response?.data;
  if (Array.isArray(body?.errors) && body.errors.length > 0) return body.errors.join(' ');
  if (body?.error) return body.error;
  return fallback;
};

const getManualSendTooltip = (row: StatementScheduledEmail): string => {
  if (row.schedule_status === 'manual_locked') {
    return `Manual send available in ${relativeTimeUntil(row.manual_send_locked_until)}`;
  }
  if (row.schedule_status === 'no_recipients') return 'No email addresses';
  return 'Manual send unavailable';
};

const SendAction: React.FC<SendActionProps> = ({ row, isSending, sendingDisabled, onSendNow }) => {
  if (sendingDisabled) {
    const tooltipTitle = 'Statement sending is disabled for this location.';
    return (
      <span {...scheduledEmailTooltipProps(tooltipTitle)} style={{ display: 'inline-flex' }}>
        <IconButton size="small" aria-label={tooltipTitle} disabled sx={{ cursor: 'default' }}>
          <SendIcon fontSize="small" />
        </IconButton>
      </span>
    );
  }
  if (!row.can_send_manually) {
    const tooltipTitle = getManualSendTooltip(row);
    return (
      <span {...scheduledEmailTooltipProps(tooltipTitle)} style={{ display: 'inline-flex' }}>
        <IconButton size="small" aria-label={tooltipTitle} disabled sx={{ cursor: 'default' }}>
          <SendIcon fontSize="small" />
        </IconButton>
      </span>
    );
  }
  const tooltipTitle = isSending ? 'Sending...' : 'Send Now';
  return (
    <span {...scheduledEmailTooltipProps(tooltipTitle)} style={{ display: 'inline-flex' }}>
      <IconButton
        size="small"
        color="primary"
        aria-label={tooltipTitle}
        onClick={(e) => {
          e.stopPropagation();
          onSendNow(row);
        }}
        disabled={isSending}
      >
        {isSending ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
      </IconButton>
    </span>
  );
};

interface ResumeActionProps {
  row: StatementScheduledEmail;
  isResuming: boolean;
  onResume: (email: StatementScheduledEmail) => void;
}

const ResumeAction: React.FC<ResumeActionProps> = ({ row, isResuming, onResume }) => {
  const canResume = row.schedule_status === 'auto_skipped' || row.schedule_status === 'auto_paused';
  if (!canResume) return null;
  const tooltipTitle = isResuming ? 'Resuming...' : 'Resume automatic sending';
  return (
    <span {...scheduledEmailTooltipProps(tooltipTitle)} style={{ display: 'inline-flex' }}>
      <IconButton
        size="small"
        color="primary"
        aria-label={tooltipTitle}
        onClick={(e) => {
          e.stopPropagation();
          onResume(row);
        }}
        disabled={isResuming}
      >
        {isResuming ? <CircularProgress size={16} /> : <PlayCircleOutlineIcon fontSize="small" />}
      </IconButton>
    </span>
  );
};

const SCHEDULE_STATUS_SORT_ORDER: Record<StatementScheduledEmail['schedule_status'], number> = {
  ready: 0,
  manual_locked: 1,
  auto_skipped: 2,
  auto_paused: 3,
  no_recipients: 4,
};

const buildColumns = (
  actionInFlight: ActionInFlight | null,
  sendingDisabled: boolean,
  onSendNow: (email: StatementScheduledEmail) => void,
  onResume: (email: StatementScheduledEmail) => void,
) => [
  { name: 'Company', selector: 'company', sortable: true, grow: 2 },
  { name: 'Subject', selector: 'subject', sortable: true, grow: 3 },
  {
    name: 'Next Send',
    selector: 'next_send_at',
    sortable: true,
    sortFunction: (a: StatementScheduledEmail, b: StatementScheduledEmail) => {
      const aTime = a.next_send_at ? new Date(a.next_send_at).getTime() : Number.POSITIVE_INFINITY;
      const bTime = b.next_send_at ? new Date(b.next_send_at).getTime() : Number.POSITIVE_INFINITY;
      return aTime - bTime;
    },
    cell: (row: StatementScheduledEmail) => row.next_send_at ?? '—',
    width: '180px',
  },
  {
    name: 'Status',
    selector: 'schedule_status',
    sortable: true,
    sortFunction: (a: StatementScheduledEmail, b: StatementScheduledEmail) =>
      SCHEDULE_STATUS_SORT_ORDER[a.schedule_status] - SCHEDULE_STATUS_SORT_ORDER[b.schedule_status],
    cell: (row: StatementScheduledEmail) => <StatementScheduleStatusCell row={row} />,
    width: '240px',
    allowOverflow: true,
    ignoreRowClick: true,
  },
  {
    name: '',
    cell: (row: StatementScheduledEmail) => (
      <Box sx={{ display: 'inline-flex', gap: 0.5 }}>
        <ResumeAction
          row={row}
          isResuming={actionInFlight?.action === 'resume' && actionInFlight.billCode === row.bill_code}
          onResume={onResume}
        />
        <SendAction
          row={row}
          isSending={actionInFlight?.action === 'send' && actionInFlight.billCode === row.bill_code}
          sendingDisabled={sendingDisabled}
          onSendNow={onSendNow}
        />
      </Box>
    ),
    width: '110px',
    allowOverflow: true,
    ignoreRowClick: true,
  },
];

const LocationTab: React.FC<LocationTabProps> = ({ location, enabled }) => {
  const { data, isLoading, isError, refetch } = useStatementScheduledEmails(location, enabled);
  const {
    data: configResp,
    isLoading: isConfigLoading,
    isError: isConfigError,
  } = useStatementConfig(location, enabled);
  const sendingDisabled =
    isConfigLoading || isConfigError || configResp?.data?.sending_enabled !== true;
  const attachInvoicesDisabled = configResp?.data?.attach_invoices === false;
  const { sendEmail } = useSendStatementEmail();
  const { resumeAutomatic } = useResumeStatementAutomatic();
  const { showSuccessSnackBar, showErrorSnackBar } = useContext(SnackBarContext);
  const [emails, setEmails] = useState<StatementScheduledEmail[]>([]);
  const [filters, setFilters] = useState<StatementScheduledEmailFilterFields>(EMPTY_STATEMENT_FILTERS);
  const [selectedEmail, setSelectedEmail] = useState<StatementScheduledEmail | null>(null);
  const [actionInFlight, setActionInFlight] = useState<ActionInFlight | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState>({ open: false, action: 'send', email: null });

  const filteredEmails = useMemo(
    () => filterStatementScheduledEmails(emails, filters),
    [emails, filters],
  );

  const handleClearFilters = () => setFilters(EMPTY_STATEMENT_FILTERS);

  useEffect(() => {
    if (data?.data) setEmails(data.data);
  }, [data]);

  const handleSendNow = (email: StatementScheduledEmail, overrideBody?: string) => {
    setSelectedEmail(null);
    setConfirm({ open: true, action: 'send', email, overrideBody });
  };

  const handleResumeAutomatic = (email: StatementScheduledEmail) => {
    setSelectedEmail(null);
    setConfirm({ open: true, action: 'resume', email });
  };

  const closeConfirm = () => setConfirm({ open: false, action: 'send', email: null });

  const handleConfirm = async () => {
    if (!confirm.email) return;
    const target = confirm.email;
    const action = confirm.action;
    const overrideBody = confirm.overrideBody;
    closeConfirm();
    setActionInFlight({ billCode: target.bill_code, action });
    try {
      if (action === 'send') {
        await sendEmail({
          location: target.location,
          bill_code: target.bill_code,
          ...(overrideBody ? { override_body: overrideBody } : {}),
        });
        await refetch();
        showSuccessSnackBar?.(`Sent statement email to ${target.company}.`);
      } else {
        await resumeAutomatic({ location: target.location, bill_code: target.bill_code });
        await refetch();
        showSuccessSnackBar?.(`Resumed automatic sending for ${target.company}.`);
      }
    } catch (err) {
      const fallback =
        action === 'send'
          ? 'Failed to send email. Please try again.'
          : 'Failed to resume automatic sending. Please try again.';
      showErrorSnackBar?.(apiErrorMessage(err, fallback));
    } finally {
      setActionInFlight(null);
    }
  };

  if (isLoading) return <Box sx={{ p: 4, textAlign: 'center' }}>Loading...</Box>;
  if (isError)
    return (
      <Box sx={{ p: 4, textAlign: 'center', color: 'error.main' }}>
        Failed to load scheduled statement emails. Make sure a configuration exists for this location.
      </Box>
    );

  const confirmTitle = confirm.action === 'send' ? 'Send Statement Now?' : 'Resume Automatic Sending?';
  const confirmBody =
    confirm.action === 'send' ? (
      <>
        Send the statement email to <strong>{confirm.email?.company}</strong> now? This cannot be undone.
      </>
    ) : (
      <>
        Re-enable automatic sending for <strong>{confirm.email?.company}</strong>? The next automatic email will be
        scheduled for the next slot from today.
      </>
    );
  const confirmCta = confirm.action === 'send' ? 'Send' : 'Resume';
  const confirmDisabled = actionInFlight !== null;

  const noDataMessage =
    emails.length === 0 ? 'No scheduled statement emails for this location.' : 'No emails match the current filters.';

  return (
    <>
      <ScheduledEmailTableTooltip />
      {sendingDisabled && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Statement sending is disabled for {location}. Scheduled and manual sends are paused. Enable it under Statements
          → Configuration.
        </Alert>
      )}
      {attachInvoicesDisabled && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Invoice PDF attachments are disabled for {location}. Statement emails will send without invoice PDFs. Enable
          under Statements → Configuration.
        </Alert>
      )}
      <StatementScheduledEmailFilters
        filters={filters}
        onChange={setFilters}
        onClear={handleClearFilters}
      />
      <Card sx={{ mt: 2.5 }}>
        <DataTable
          keyField="bill_code"
          columns={buildColumns(actionInFlight, sendingDisabled, handleSendNow, handleResumeAutomatic)}
          data={filteredEmails}
          pagination
          paginationPerPage={25}
          paginationRowsPerPageOptions={[25, 50, 100]}
          highlightOnHover
          striped
          noHeader
          onRowClicked={(row: StatementScheduledEmail) => setSelectedEmail(row)}
          pointerOnHover
          conditionalRowStyles={[
            {
              when: (row: StatementScheduledEmail) =>
                row.schedule_status === 'auto_skipped' || row.schedule_status === 'auto_paused',
              style: { backgroundColor: '#f5f5f5' },
            },
          ]}
          noDataComponent={<Box sx={{ p: 3, color: 'text.secondary' }}>{noDataMessage}</Box>}
        />
      </Card>

      <StatementScheduledEmailModal
        email={selectedEmail}
        sendingDisabled={sendingDisabled}
        locationAttachInvoices={!attachInvoicesDisabled}
        onClose={() => setSelectedEmail(null)}
        onSendNow={handleSendNow}
        onResumeAutomatic={handleResumeAutomatic}
      />

      <Dialog open={confirm.open} onClose={closeConfirm}>
        <DialogTitle>{confirmTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmBody}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleConfirm} disabled={confirmDisabled}>
            {confirmDisabled ? <CircularProgress size={16} /> : confirmCta}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const StatementScheduledEmailsPage: React.FC = () => {
  const [location] = useState(() => getCollectionsSelectedLocation());

  return (
    <Grid container>
      <Grid item xs={12}>
        <PageHeader mainTitle="Statements — Scheduled Emails" titleIcon={<SendIcon />} />
      </Grid>
      <Grid item xs={12} sx={{ mt: 2 }}>
        <LocationTab location={location} enabled />
      </Grid>
    </Grid>
  );
};

export default StatementScheduledEmailsPage;
